import React, {
  forwardRef,
  ForwardRefExoticComponent,
  lazy,
  PropsWithoutRef,
  Ref,
  RefAttributes,
  Suspense,
} from 'react';

import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

// Convenience wrapper around Suspense. Defaults to CenteredHorizontalThrobber as the fallback.
// Returns a function component that will pass through props to the suspended component once it's loaded.
export default function suspend<P = {}>(
  Component: ReturnType<typeof lazy>,
  Fallback: React.ElementType = CenteredHorizontalThrobber
): React.FunctionComponent<P> {
  return (props: P) => (
    <Suspense fallback={<Fallback />}>
      <Component {...props} />
    </Suspense>
  );
}

// Convenience wrapper around Suspense and forwardRef. Defaults to CenteredHorizontalThrobber as the fallback.
// Returns a function component that will pass through props and ref to the suspended component once it's loaded.
export function suspendWithForwardRef<P = {}>(
  Component: React.ForwardRefExoticComponent<P>,
  Fallback: React.ElementType = CenteredHorizontalThrobber
): ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<typeof Component>> {
  return forwardRef((props: P, ref: Ref<typeof Component>) => (
    <Suspense fallback={<Fallback />}>
      <Component {...props} ref={ref} />
    </Suspense>
  ));
}
